<script setup lang="ts">
import { $dayjs } from '@/utils/dayjs'
import { reviewsList } from '~/assets/js/reviews'
import { MIN_DEFAULT_BUDGET, MAX_DEFAULT_BUDGET } from '@/helpers/priceHelper'

import { getTemplateFromRouteQuery } from '@/helpers/search/route'

import type { Locale } from 'lc-services/types'
import type {
  Bounds,
  NumericFilter,
  QueryModelType,
  ReplicasValues,
  SearchFacetObject,
  SearchFacetValue,
} from '~/types/search/types'
import type { StateDestination } from '~/types/types'

const { isSmallScreen } = useBreakpoint()
const { t, locale } = useI18n<unknown, Locale>()

$dayjs.locale(locale.value)

const stateDestination = useState<StateDestination>('destination')

const seoTitle = stateDestination.value?.seoTitle || ''
const linksSeo = stateDestination.value?.linksSeo || []
const destination = stateDestination.value?.destination || {}
const queryOutput = stateDestination.value?.queryOutput || []

const localePath = useLocalePath()
const route = useRoute()
const router = useRouter()
const { setFiltersCount } = useSearchFiltersCount()
const { currency } = useUser()
const {
  data,
  setBreadcrumbsDestinationId,
  setData,
  setSubmittedSearch,
  submittedSearch,
} = useSearchInput()

const searchContainer = ref<HTMLElement | null>(null)
const showFiltersModal = ref(false)
const template = ref<'list' | 'map' | 'split'>(
  getTemplateFromRouteQuery(route.query),
)
const modalFiltersOpen = ref(false)
const clickPageNext = ref(false)
const hoveredHouseId = ref<number>()
const lastSearchQuery = ref(route.query)
const requestInfo = { lastId: 0 }

const {
  bathrooms,
  bedrooms,
  capacity,
  setNumericFilter,
  clearNumericFilterByType,
} = useSearchNumericFilters()

const {
  newFacetFilters,
  facetsFiltersParams,
  facetFilters,
  clearAllFacetFilters,
  setFacetFilter,
} = useSearchFacetFilters()

const {
  clearDateFilters,
  setDateStart,
  setDateEnd,
  startDate,
  endDate,
  hasDates,
} = useSearchDateFilters()

const { minBudget, maxBudget, price, clearBudgetFilters, setBudgetFilters } =
  useSearchBudgetFilters()

const { currentReplica, setReplicaFilters } = useSearchReplicaFilters()

const {
  queryValue,
  apiParamsDestinationIds,
  clearLocationFilters,
  setLocation,
} = useSearchLocationFilters()

const {
  coordinatesBoundaries,
  facets,
  fetchSearchableTagsIdentifiers,
  fetchSplitResults,
  firstLoad,
  hasResultsSplit,
  hitsCoordinatesMap,
  housesPeriodIds,
  housesPerPage,
  isLoading,
  keepOldResults,
  nbHits,
  nbPages,
  page,
  resetHousesPeriodIds,
  searchableTagsIdentifiers,
  setFirstLoad,
  setIsLoading,
  setKeepOldResults,
  setPage,
  showPreviousButton,
} = useSearchResults({ startDate, endDate })

const { hitsListIds, hideBannerHotDeals } = useSearchClientResults({ hasDates })

const { currentHouseFavoriteSelected } = useSearchWishlist()
const {
  bounds,
  geographicAreaActive,
  onMapMoved,
  resetBounds,
  resetHousesStorage,
  setBounds,
  setHousesStorage,
} = useSearchSplitMap()
const {
  trackFavoritePeriod,
  trackResetFilter,
  trackSearchHouse,
  trackMapEnlarged,
  trackSearchMap,
  trackSearchResultsViewed,
} = useSearchTracking()
const { updateRouter } = useSearchUpdateRouter()
const {
  favoritePeriod,
  getClosestFavoritePeriod,
  isCorrectHousePeriod,
  isFavoritePeriodWeekly,
  nearPeriods,
} = useSearchFavoritePeriod()

const { fetchAndFillStates } = useSearchInitStates({
  typeSearch: 'public',
})

const dateChanged = computed(
  () =>
    lastSearchQuery.value.startDate !== route.query.startDate ||
    lastSearchQuery.value.endDate !== route.query.endDate,
)
const budgetChanged = computed(
  () =>
    lastSearchQuery.value.minPrice !== route.query.minPrice ||
    lastSearchQuery.value.maxPrice !== route.query.maxPrice,
)
const destinationChanged = computed(
  () => lastSearchQuery.value.destinationId !== route.query.destinationId,
)
const templateChanged = computed(
  () => lastSearchQuery.value.template !== route.query.template,
)
const indexNameChanged = computed(
  () => lastSearchQuery.value.indexName !== route.query.indexName,
)
const searchHousesPayload = computed(() => {
  const payload: QueryModelType = {
    initializelist: !keepOldResults.value,
    mode: 'client',
    bathrooms: bathrooms.value ? bathrooms.value.toString() : '1',
    bedrooms: bedrooms.value ? bedrooms.value.toString() : '1',
    capacity: capacity.value ? capacity.value.toString() : '1',
    itemsPerPage: housesPerPage.value,
    replica: currentReplica.value.label,
  }

  if (apiParamsDestinationIds.value.length) {
    payload.destinationIds = apiParamsDestinationIds.value
  }
  if (page.value > 0) {
    payload.page = page.value
  }
  if (newFacetFilters.value?.size) {
    payload.facetFilters = newFacetFilters.value
  }
  if (price.value.length) {
    payload.prices = price.value
  }
  if (startDate.value && endDate.value) {
    payload.period = {
      startDate: startDate.value,
      endDate: endDate.value,
    }
  }

  if (geographicAreaActive.value) {
    payload.insideBoundingBox = [bounds.value]
    payload.destinationIds = []
  }

  payload.housesPeriodIds = housesPeriodIds.value

  return payload
})
const showPaginate = computed(() => nbPages.value > page.value + 1)
const staticReviews = computed(() => reviewsList[locale.value])
const searchLinksTitle = computed(() => {
  const destinationType = destination.searchUrl?.[locale.value]
    ?.split('/')?.[0]
    ?.split('-')?.[1]

  return t('search.typesOfVillas', {
    destination: destination.name,
    houseType: destinationType,
  })
})
const countTotalFiltersSelected = computed(() => {
  let total = 0
  if (bathrooms.value) total += 1
  if (bedrooms.value) total += 1
  if (minBudget.value !== MIN_DEFAULT_BUDGET) total += 1
  if (maxBudget.value !== MAX_DEFAULT_BUDGET) total += 1
  total += facetsFiltersParams.value.length || 0
  return total
})
const colsDesktop = computed(() => {
  if (template.value === 'list') return '8'

  return template.value === 'split' ? '12' : '6'
})
const isSearchingUnknownDestination = computed(
  () => geographicAreaActive.value && data.value.location.id === 'area_unknown',
)

onBeforeMount(() => {
  if (!showPreviousButton.value && page.value > 1) {
    const query = route.query

    delete query.page
    router.push({ query: { ...query } })
  }
})

onMounted(() => {
  scrollTop()
  setFiltersCount(countTotalFiltersSelected.value)
})

onBeforeRouteLeave(() => {
  resetHousesPeriodIds()
  resetHousesStorage()
})

const { execute, status } = await useAsyncData(
  'search-client-async-data',
  async () => {
    ++requestInfo.lastId

    if (!Object.keys(route.query).length) {
      setFirstLoad(true)
    }

    setIsLoading(true)

    if (
      budgetChanged.value ||
      dateChanged.value ||
      destinationChanged.value ||
      indexNameChanged.value
    ) {
      resetHousesPeriodIds()
      resetHousesStorage()
    }

    await fetchAndFillStates(
      firstLoad.value
        ? { id: destination.id, name: destination.name, queryOutput }
        : undefined,
    )
    const housesRes = await fetchSplitResults({
      currency,
      searchHousesPayload: searchHousesPayload.value,
      requestInfo,
    })

    await fetchSearchableTagsIdentifiers()

    setHousesStorage(housesRes)
    setFirstLoad(false)

    return true
  },
)

watch(
  status,
  (value) => {
    if (value === 'pending') {
      if (clickPageNext.value) window.scrollTo(0, window.scrollY)
      else window.scrollTo(0, 0)
    }

    if (value === 'success') {
      clickPageNext.value = false

      trackSearchResultsViewed({
        isCorrectHousePeriod: hasDates.value
          ? isCorrectHousePeriod.value
          : undefined,
        geographicArea: geographicAreaActive.value,
        template: template.value,
        searchableTagsIdentifiers: searchableTagsIdentifiers.value,
      })
    }
  },
  { immediate: import.meta.client },
)

watch(
  () => route.query,
  (_, oldQuery) => {
    lastSearchQuery.value = { ...oldQuery }

    if (templateChanged.value) {
      template.value = getTemplateFromRouteQuery(route.query)
      return
    }

    execute()
  },
  { deep: true },
)

watch(countTotalFiltersSelected, (val) => {
  setFiltersCount(val)
})

watch(currency, async (_) => {
  await updateStateAndUpdateRoute()
})

watch(showFiltersModal, (newValue) => {
  if (newValue) scrollTop()
})

watch(submittedSearch, async (value) => {
  if (value) {
    updateDataToState()
    if (
      data.value.location.id?.toString().startsWith('area') &&
      data.value.location.coords
    )
      setBounds(buildBoundingBox(data.value.location.coords))
    else resetBounds()
    await updateStateAndUpdateRoute()
    setSubmittedSearch(false)
  }
})

const resetFilters = async (
  source: 'filters sticky' | 'filters modale' | 'search no result',
) => {
  clearBudgetFilters()
  clearAllFacetFilters()
  clearNumericFilterByType('bathrooms')
  clearNumericFilterByType('bedrooms')

  await updateStateAndUpdateRoute()

  trackResetFilter(source)
}

const resetFiltersNoResult = () => {
  if (geographicAreaActive.value) {
    resetBounds()
    setData('location', {})
  }
  resetFilters('search no result')
}

const clearFilterNumeric = async (type: NumericFilter) => {
  clearNumericFilterByType(type)

  await updateStateAndUpdateRoute()
}

const changeTemplate = async (type: 'list' | 'split' | 'map') => {
  if (type === 'map') scrollTop()
  template.value = type

  await updateRouterOnly()
}

const mapMoved = (bounds: Bounds) =>
  onMapMoved(bounds, async () => {
    await updateStateAndUpdateRoute()
    setGeographicAreaInputText()
  })

const filteredNumeric = async (
  attribute: NumericFilter,
  selectedFilter: number,
) => {
  setNumericFilter(attribute, selectedFilter)
  await updateStateAndUpdateRoute()
}

const paginate = async (
  pageDirection: 'next-page' | 'previous-page' = 'next-page',
) => {
  // we need to set previousPage and nextPage so that if multiple pages are already visible we don't duplicate content
  if (pageDirection === 'previous-page') {
    setPage(page.value - 1)
  } else {
    setPage(page.value + 1)
  }

  if (pageDirection === 'next-page') clickPageNext.value = true

  await updateStateAndUpdateRoute(pageDirection === 'next-page')
}

const refineFacets = async (
  facet: SearchFacetObject<SearchFacetValue<string>[], true>,
  facetValue: { checked: boolean; value: string },
) => {
  facetValue.checked = !facetValue.checked

  setFacetFilter(facet, facetValue)

  await updateStateAndUpdateRoute()
}

const handleSetFavoritePeriod = (period: 'previous' | 'next') => {
  const id = queryValue.value?.value?.toString()
  const name = queryValue.value?.label
  if (id && name)
    trackFavoritePeriod({
      id,
      name,
    })
  const [periodStart, periodEnd] = getClosestFavoritePeriod(period)
  if (!periodStart || !periodEnd) return

  setData('startDate', periodStart)
  setData('endDate', periodEnd)
  setSubmittedSearch(true)
}

const sortByReplica = async (replica: {
  label: ReplicasValues
  value: string
}) => {
  setReplicaFilters(replica)

  await updateStateAndUpdateRoute()
}

const updateBudget = async (maxB: string, minB: string) => {
  setBudgetFilters(minB, maxB)

  await updateStateAndUpdateRoute()
}

const updateRooms = async (rooms: { bathrooms: number; bedrooms: number }) => {
  if (rooms.bathrooms) setNumericFilter('bathrooms', rooms.bathrooms)
  else clearNumericFilterByType('bathrooms')

  if (rooms.bedrooms) setNumericFilter('bedrooms', rooms.bedrooms)
  else clearNumericFilterByType('bedrooms')

  await updateStateAndUpdateRoute()
}

const trackHouse = (houseId: number) => {
  trackSearchHouse({
    houseId,
    mapVisible: template.value === 'map',
    searchableTagsIdentifiers: searchableTagsIdentifiers.value,
  })
}

const toggleFiltersModal = () => {
  showFiltersModal.value = !showFiltersModal.value
}

const updateStateAndUpdateRoute = async (nextPage = false) => {
  if (nextPage) {
    setKeepOldResults(true)
  } else {
    setKeepOldResults(false)
    if (page.value >= 1) setPage(0)
  }

  await updateRouterOnly()
}

const updateRouterOnly = async () => {
  await updateRouter(template.value)
}

if (
  route?.name?.toString()?.includes('destination-slug-filter') &&
  nbHits.value === 0
) {
  const path = localePath({
    name: 'destination-name',
    params: { name: route.params.slug },
  })

  navigateTo(path, { redirectCode: 301 })
}

const scrollTop = () => {
  window.scrollTo(0, 0)
}

const setGeographicAreaInputText = () => {
  setData('location', { id: 'area', name: t('search.geographicArea') })
  setBreadcrumbsDestinationId()
}

const updateDataToState = () => {
  setNumericFilter('capacity', data.value.guest)

  if (data.value.location.name && data.value.location.id) {
    setLocation(
      data.value.location.name,
      Number(data.value.location.id),
      data.value.location.coords,
    )
  } else clearLocationFilters()

  if (data.value.startDate && data.value.endDate) {
    setDateStart(data.value.startDate)
    setDateEnd(data.value.endDate)
  } else clearDateFilters()
}

const mapEnlargedHandler = () => {
  changeTemplate('map')

  trackMapEnlarged()
}

const mapClosedHandler = () => {
  trackSearchMap({ event: 'map_close' })

  changeTemplate('list')
}
</script>

<template>
  <div class="mb-4 w-full">
    <div ref="searchContainer">
      <div
        v-if="isLoading"
        class="fixed inset-x-0 top-0 z-fixed bg-gray-200/90"
      />

      <SearchTopBarV2Mobile
        v-if="isSmallScreen"
        :template="template"
        @toggle-filters-modal="showFiltersModal = true"
        @toggle-map="changeTemplate('split')"
        @update-budget="updateBudget"
        @update-rooms="updateRooms"
      />

      <div
        :class="[
          'mb-4',
          {
            'flex flex-col-reverse lg:grid lg:grid-cols-2':
              template === 'split',
            'sm:flex': template === 'list',
          },
        ]"
      >
        <div
          v-if="!isSmallScreen && hasResultsSplit && template === 'list'"
          class="sticky top-[calc(95px+1rem)] h-full w-[266px] flex-none pl-6"
          data-cy="search-sidebar-filter"
        >
          <SearchFilterSidebar
            class="max-h-[calc(100vh-130px-2rem)] pr-4"
            :bathrooms="bathrooms"
            :bedrooms="bedrooms"
            :facet-filters="facetFilters"
            :facets="facets"
            :loading-facets="isLoading"
            :max-budget="maxBudget"
            :min-budget="minBudget"
            show-reset-filter
            @changeFacet="refineFacets"
            @clear-filter-numeric="clearFilterNumeric"
            @refine-filter-numeric="filteredNumeric"
            @update-budget="updateBudget"
            @reset-filters="resetFilters('filters sticky')"
          />
        </div>

        <div
          :class="[
            {
              'flex-1 laptop:w-[calc(100%-266px-1.5rem)]': template === 'list',
            },
          ]"
        >
          <SearchResultHeaderV2
            v-if="!isSmallScreen"
            :hide-sorter="nbHits === 0"
            :nb-hits="nbHits"
            :template="template"
            @change-template="changeTemplate"
            @change-replica="sortByReplica"
            @open-filters="modalFiltersOpen = true"
            @update-budget="updateBudget"
            @update-rooms="updateRooms"
          />

          <div
            v-if="template === 'list' || template === 'split'"
            class="mb-4 sm:mb-0 sm:px-4 lg:px-6"
          >
            <SearchNoResult
              v-if="!hasResultsSplit"
              :loading="isLoading"
              :template="template"
              @clear-refinements="resetFiltersNoResult"
            />

            <BaseRow v-else id="search-results" class="mx-0 md:-mx-2">
              <BaseCol cols="24">
                <h2
                  v-if="isSearchingUnknownDestination"
                  class="mb-4 mt-6 px-2 text-xl sm:mb-5 sm:mt-6 sm:px-0"
                >
                  {{ $t('search.nearbyLocationHouses') }}
                </h2>
                <SearchHotDeals v-if="!hideBannerHotDeals" class="px-4" />

                <SearchClientGridResults
                  v-model="hoveredHouseId"
                  :center-point="
                    isSearchingUnknownDestination ? data.location.coords : null
                  "
                  :cols-desktop="colsDesktop"
                  :is-favorite-period-weekly="isFavoritePeriodWeekly"
                  :searchable-tags-identifiers="searchableTagsIdentifiers"
                  :map-visible="template === 'split'"
                  :favorite-period="favoritePeriod"
                  :near-periods="nearPeriods"
                  @click-favorite-period="handleSetFavoritePeriod"
                  @paginate="paginate"
                />
              </BaseCol>
            </BaseRow>

            <div>
              <div v-if="queryValue.value" :class="{ 'mt-6': showPaginate }">
                <SearchWaitingListCard
                  :bathrooms="bathrooms"
                  :bedrooms="bedrooms"
                  :capacity="capacity"
                  :currency="currency"
                  :destination-id="queryValue.value"
                  :destination-name="queryValue.label"
                  :end-date="endDate"
                  :max-budget="maxBudget"
                  :min-budget="minBudget"
                  :start-date="startDate"
                  :show-image="template === 'list'"
                  class="mt-6"
                />
              </div>

              <HouseReviews
                v-if="hasResultsSplit && !isSearchingUnknownDestination"
                class="mx-0 mt-6"
                full-width
                :reviews="staticReviews"
              />

              <BaseRow v-if="seoTitle" class="mx-0 my-12">
                <BaseCol class="px-0">
                  <h1 class="text-center text-5xl">
                    {{ seoTitle }}
                  </h1>

                  <SearchLinks
                    v-if="
                      linksSeo?.length &&
                      stateDestination.destination.housesCount
                    "
                    :links-seo="linksSeo"
                    :title="searchLinksTitle"
                    class="mt-10"
                  />
                </BaseCol>
              </BaseRow>
            </div>
          </div>
        </div>
        <div
          v-if="
            template !== 'map' &&
            isSmallScreen &&
            !isSearchingUnknownDestination
          "
          class="flex items-center justify-between p-4 lg:hidden"
        >
          <div class="min-w-[120px] text-xs font-semibold">
            {{ $t('search.propertyCount', nbHits) }}
          </div>
          <SearchResultHeaderSorter @change-replica="sortByReplica" />
        </div>
        <div
          v-show="template !== 'list'"
          :class="[
            isSmallScreen && template === 'map'
              ? 'fixed inset-0 z-[9998]'
              : 'relative lg:sticky lg:top-[72px] lg:h-[calc(100vh-72px)]',
            {
              'h-full lg:mt-[-74px]': template === 'map',
            },
            { 'h-[204px]': template !== 'map' },
          ]"
        >
          <div
            v-if="!isSmallScreen"
            class="absolute right-6 top-6 z-50 flex gap-2"
          >
            <button
              v-if="template !== 'map'"
              class="flex size-7 items-center justify-center rounded-full bg-white shadow-normal"
              type="button"
              @click="mapEnlargedHandler"
            >
              <BaseIcon name="expand" :size="1" />
            </button>
            <button
              v-if="template === 'split'"
              class="flex size-7 items-center justify-center rounded-full bg-white shadow-normal"
              type="button"
              @click="mapClosedHandler"
            >
              <BaseIcon name="close" :size="1.3" />
            </button>
          </div>

          <div
            v-if="isSmallScreen && template !== 'map'"
            class="absolute top-0 z-20 size-full bg-white/10"
            @click="changeTemplate('map')"
          />
          <LazySearchMapV2
            class="h-full"
            :coordinates-boundaries="coordinatesBoundaries"
            :coordinates-hits-map="hitsCoordinatesMap"
            :geographic-area-active="
              geographicAreaActive &&
              data.location.name === $t('search.geographicArea')
            "
            :hide-zoom="isSmallScreen && template !== 'map'"
            :hits-list-ids="hitsListIds"
            :hovered-house-id="hoveredHouseId"
            :is-loading="isLoading"
            :search-houses-payload="searchHousesPayload"
            :template="template"
            @map-moved="mapMoved"
            @track-house="trackHouse"
          />
        </div>
      </div>
    </div>

    <SearchClientModalFilters
      v-if="!isSmallScreen && template !== 'list'"
      v-model="modalFiltersOpen"
      @change-facet="refineFacets"
      @clear-filter-numeric="clearFilterNumeric"
      @refine-filter-numeric="filteredNumeric"
      @reset-filters="resetFilters('filters modale')"
      @update-budget="updateBudget"
    />

    <SearchFilterModalMobile
      v-model="showFiltersModal"
      class="!z-[9999]"
      :bathrooms-quantity="bathrooms"
      :bedrooms-quantity="bedrooms"
      :capacity-quantity="capacity"
      :facet-filters="facetFilters"
      :max-budget="maxBudget"
      :min-budget="minBudget"
      :facets="facets"
      :loading-facets="isLoading"
      :nb-hits="nbHits"
      @change-facet="refineFacets"
      @clear-all="resetFilters('filters modale')"
      @clear-filter-numeric="clearFilterNumeric"
      @refine-filter-numeric="filteredNumeric"
      @toggle-filters="toggleFiltersModal"
      @update-budget="updateBudget"
    />

    <WishlistMultipleModal v-bind="currentHouseFavoriteSelected" />
  </div>
</template>
